/**
 * Control4 Local API Integration
 *
 * Auth flow:
 * 1. POST to Netlify Function proxy which handles Control4 cloud auth
 * 2. Get director bearer token back (valid 24h)
 * 3. Use director token to call local controller REST API
 */

const CONTROLLER_IP = '192.168.254.69';
const LOCAL_BASE = `https://${CONTROLLER_IP}`;

const STORAGE_KEY = 'c4_director_token';
const STORAGE_EXPIRY_KEY = 'c4_director_token_expiry';
const STORAGE_CONTROLLER_KEY = 'c4_controller_name';
const STORAGE_CREDS_KEY = 'c4_credentials';

// Netlify Function handles all cloud auth (avoids CORS issues)
const AUTH_PROXY = '/.netlify/functions/control4-auth';

/**
 * Full auth flow via Netlify Function proxy.
 * Stores director token + expiry in localStorage.
 */
export async function authenticateControl4(username, password) {
  const res = await fetch(AUTH_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json();

  if (!res.ok || !data.token) {
    const msg = data.debug ? `${data.error}: ${data.debug}` : (data.error || 'Control4 authentication failed');
    throw new Error(msg);
  }

  localStorage.setItem(STORAGE_KEY, data.token);
  localStorage.setItem(STORAGE_EXPIRY_KEY, String(Date.now() + (data.validSeconds || 86400) * 1000));
  localStorage.setItem(STORAGE_CONTROLLER_KEY, data.controllerName);
  // Store credentials for auto-re-auth when token expires
  localStorage.setItem(STORAGE_CREDS_KEY, JSON.stringify({ username, password }));

  return data.token;
}

// --- Local Director API (via Netlify Function proxy) ---

const C4_PROXY = '/.netlify/functions/control4-proxy';

function getDirectorToken() {
  const token = localStorage.getItem(STORAGE_KEY);
  const expiry = localStorage.getItem(STORAGE_EXPIRY_KEY);
  if (!token || !expiry || Date.now() > Number(expiry)) {
    return null;
  }
  return token;
}

export function isControl4Authenticated() {
  return !!getDirectorToken();
}

export function clearControl4Auth() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_EXPIRY_KEY);
  localStorage.removeItem(STORAGE_CONTROLLER_KEY);
  localStorage.removeItem(STORAGE_CREDS_KEY);
}

/**
 * Re-authenticate automatically using stored credentials.
 * Returns true if re-auth succeeded, false if no stored creds or auth failed.
 */
export async function ensureAuthenticated() {
  if (getDirectorToken()) return true; // still valid
  const raw = localStorage.getItem(STORAGE_CREDS_KEY);
  if (!raw) return false; // never logged in — need manual login
  try {
    const { username, password } = JSON.parse(raw);
    await authenticateControl4(username, password);
    return true;
  } catch {
    return false;
  }
}

async function directorGet(path) {
  const token = getDirectorToken();
  if (!token) throw new Error('Not authenticated with Control4');

  const res = await fetch(`${C4_PROXY}?path=${encodeURIComponent(path)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

async function directorPost(path, body) {
  const token = getDirectorToken();
  if (!token) throw new Error('Not authenticated with Control4');

  const res = await fetch(`${C4_PROXY}?path=${encodeURIComponent(path)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

/**
 * List all items on the controller (devices, macros, etc.)
 */
export async function getItems() {
  return directorGet('/api/v1/items');
}

/**
 * Get commands available for an item
 */
export async function getItemCommands(itemId) {
  return directorGet(`/api/v1/items/${itemId}/commands`);
}

/**
 * Execute a command on an item (macro, device, etc.)
 */
export async function executeCommand(itemId, command, params = {}) {
  return directorPost(`/api/v1/items/${itemId}/commands`, {
    async: false,
    command,
    tParams: params,
  });
}

/**
 * Get variables for an item (e.g. POWER_STATE)
 */
export async function getItemVariables(itemId, varNames) {
  const query = varNames ? `?varnames=${encodeURIComponent(varNames)}` : '';
  return directorGet(`/api/v1/items/${itemId}/variables${query}`);
}

/**
 * Get bindings for an item (input/output connections)
 */
export async function getItemBindings(itemId) {
  return directorGet(`/api/v1/items/${itemId}/bindings`);
}
